import { clsx } from "clsx";
import { HTMLAttributes, ReactNode, useMemo } from "react";
import { Icon, type IconProps } from "../Icons";
import { Body } from "../Typography";
import "./Tip.css";

export interface TipContentSegment {
  id?: string | number;
  text: string;
  onClick?: () => void;
  ariaLabel?: string;
}

export interface TipInlineAction {
  id?: string | number;
  matchText: string;
  onClick: () => void;
  occurrence?: number | "first" | "last";
  ariaLabel?: string;
  caseSensitive?: boolean;
}

export interface TipProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "content"> {
  content?: string | TipContentSegment[];
  inlineActions?: TipInlineAction[];
  iconName?: IconProps["name"];
  icon?: ReactNode;
  iconColor?: string;
  hideIcon?: boolean;
  className?: string;
}

export const Tip = ({
  content,
  inlineActions,
  icon,
  iconName = "lightbulb",
  iconColor,
  hideIcon = false,
  className,
  ...rest
}: TipProps) => {
  const segments = useMemo(() => {
    if (Array.isArray(content)) {
      return content;
    }

    const text = content ?? "";

    if (!text) {
      return [];
    }

    if (inlineActions?.length) {
      return createSegmentsFromInlineActions(text, inlineActions);
    }

    return [{ text }];
  }, [content, inlineActions]);

  return (
    <div className={clsx("indigo-tip", className)} {...rest}>
      {hideIcon ? null : (
        <span
          className="indigo-tip-icon-wrapper"
          style={iconColor ? { color: iconColor } : undefined}
          aria-hidden="true"
        >
          {icon ?? (
            <Icon name={iconName} size="xs" className="indigo-tip-icon" />
          )}
        </span>
      )}
      <Body size="sm" className="indigo-tip-text">
        {segments.length === 0
          ? null
          : segments.map((segment, index) => {
              if (!segment.onClick) {
                return (
                  <span
                    key={segment.id ?? `tip-text-${index}`}
                    className="indigo-tip-copy"
                  >
                    {segment.text}
                  </span>
                );
              }

              const ariaLabel =
                segment.ariaLabel || segment.text.trim() || undefined;

              return (
                <button
                  key={segment.id ?? `tip-action-${index}`}
                  type="button"
                  className="indigo-tip-action"
                  onClick={segment.onClick}
                  aria-label={ariaLabel}
                >
                  {segment.text}
                </button>
              );
            })}
      </Body>
    </div>
  );
};

Tip.displayName = "Tip";

type InlineActionMatch = {
  start: number;
  end: number;
  action: TipInlineAction;
};

const createSegmentsFromInlineActions = (
  text: string,
  inlineActions: TipInlineAction[],
): TipContentSegment[] => {
  const matches: InlineActionMatch[] = [];

  inlineActions.forEach((action) => {
    if (!action.matchText) {
      return;
    }

    const positions = findAllOccurrences(
      text,
      action.matchText,
      action.caseSensitive,
    );

    const occurrenceIndex = resolveOccurrenceIndex(
      positions,
      action.occurrence,
    );
    const start =
      typeof occurrenceIndex === "number"
        ? positions[occurrenceIndex]
        : undefined;

    if (typeof start === "number") {
      matches.push({
        start,
        end: start + action.matchText.length,
        action,
      });
    } else {
      console.warn(
        `Tip: match "${action.matchText}" not found for inline action`,
      );
    }
  });

  const sortedMatches = matches.sort((a, b) => a.start - b.start);

  const filteredMatches: InlineActionMatch[] = [];
  let lastEnd = -1;

  sortedMatches.forEach((match) => {
    if (match.start < lastEnd) {
      console.warn(
        "Tip: overlapping inline action ignored",
        actionSummary(match.action),
      );
      return;
    }

    filteredMatches.push(match);
    lastEnd = match.end;
  });

  if (!filteredMatches.length) {
    return [{ text }];
  }

  const segments: TipContentSegment[] = [];
  let cursor = 0;

  filteredMatches.forEach((match) => {
    if (match.start > cursor) {
      segments.push({ text: text.slice(cursor, match.start) });
    }

    segments.push({
      id: match.action.id,
      text: text.slice(match.start, match.end),
      onClick: match.action.onClick,
      ariaLabel: match.action.ariaLabel,
    });

    cursor = match.end;
  });

  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor) });
  }

  return segments;
};

const findAllOccurrences = (
  source: string,
  rawSearch: string,
  caseSensitive = false,
): number[] => {
  const search = caseSensitive ? rawSearch : rawSearch.toLowerCase();
  const haystack = caseSensitive ? source : source.toLowerCase();
  const positions: number[] = [];

  if (!search) return positions;

  let index = haystack.indexOf(search);

  while (index !== -1) {
    positions.push(index);
    index = haystack.indexOf(search, index + search.length);
  }

  return positions;
};

const resolveOccurrenceIndex = (
  positions: number[],
  occurrence?: TipInlineAction["occurrence"],
): number | undefined => {
  if (!positions.length) return undefined;

  if (!occurrence || occurrence === "first") {
    return 0;
  }

  if (occurrence === "last") {
    return positions.length - 1;
  }

  const clampedOccurrence = Math.max(1, occurrence) - 1;
  return clampedOccurrence < positions.length ? clampedOccurrence : undefined;
};

const actionSummary = (action: TipInlineAction) => ({
  id: action.id,
  matchText: action.matchText,
  occurrence: action.occurrence,
});
